import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { MessageCircle, MapPin, Phone, Mail, Users, Star, Zap, Gauge, TrendingUp, Wrench, Leaf, Fuel } from 'lucide-react';
import { testimonials } from '../mock';
import CalculatorWidget from '../components/CalculatorWidget';
import { useSeo } from '../hooks/useSeo';
import { seo, LOGO_URL, faqIzmirEn } from '../seoConfig';
import LegalLinks from '../components/LegalLinks';

const WA_URL = 'https://wa.me/905327866635?text=Hello%2C%20I%20would%20like%20to%20know%20more%20about%20Powerlab%20chip%20tuning%20in%20Izmir';

const IzmirChipTuning = () => {
  useSeo(seo.izmirEn);

  const izmirStats = [
    { number: '40+', label: 'Global Dealers' },
    { number: '20+', label: 'Countries' },
    { number: '300,000+', label: 'Vehicle Files Tuned' },
    { number: 'Since 2012', label: 'Active (Estonia HQ 2019)' },
  ];

  const services = [
    { icon: Zap, title: 'Stage 1 / 2 / 3 Software', desc: 'Custom ECU remapping tailored to your goals — daily driver to full track build.' },
    { icon: Wrench, title: 'DPF / EGR / AdBlue', desc: 'Diesel emission system solutions for older or problem-prone vehicles.' },
    { icon: Leaf, title: 'E85 Bioethanol Conversion', desc: 'Greener, cheaper fuel with more power. Full flex-fuel calibration.' },
    { icon: Gauge, title: 'Dyno Testing', desc: 'Every tune verified on our chassis dyno — real numbers, real safety margins.' },
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="fixed top-0 w-full bg-black/95 backdrop-blur-md z-50 border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img src={LOGO_URL} alt="Powerlab Works" className="h-14 w-auto object-contain" />
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-300 hover:text-[#FF6600] transition-colors">Home</Link>
              <Link to="/calculator" className="text-gray-300 hover:text-[#FF6600] transition-colors">Power Calculator</Link>
              <Link to="/faq" className="text-gray-300 hover:text-[#FF6600] transition-colors">FAQ</Link>
              <Link to="/tr/izmir-chip-tuning" className="text-gray-300 hover:text-[#FF6600] transition-colors" hrefLang="tr">TR</Link>
            </nav>
            <div className="flex items-center space-x-3">
              <Button onClick={() => window.open(WA_URL, '_blank')} className="hidden sm:inline-flex bg-[#25D366] hover:bg-[#1DA851] text-white">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </Button>
              <Button onClick={() => window.location.href = 'mailto:info@powerlab.works?subject=Izmir%20Appointment&body=Hello%2C%20I%20would%20like%20to%20book%20a%20chip%20tuning%20appointment%20at%20your%20Izmir%20location.'} className="bg-[#FF6600] hover:bg-[#E55A00] text-white">
                Book Appointment
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-b from-black via-[#0a0a0a] to-black overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&q=80" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative container mx-auto px-4 text-center">
          <span className="inline-block text-[#FF6600] font-semibold text-sm uppercase tracking-widest mb-4">Izmir • Bornova</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
            Izmir's Chip Tuning &<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF6600] to-[#E55A00]">ECU Software Center</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            300,000+ processed vehicle files, 40+ dealer network worldwide, 20+ countries, active since 2012 (Estonia-registered 2019). Dyno-tested, safe and reversible ECU remaps at our Bornova workshop.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button className="w-full sm:w-auto bg-gradient-to-r from-[#FF6600] to-[#E55A00] hover:from-[#E55A00] hover:to-[#CC4A00] text-white px-8 py-6 text-lg">
                Free Performance Estimate <TrendingUp className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Button onClick={() => window.open(WA_URL, '_blank')} className="w-full sm:w-auto bg-[#25D366] hover:bg-[#1DA851] text-white px-8 py-6 text-lg">
              <MessageCircle className="mr-2 w-5 h-5" /> Chat on WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="py-12 bg-[#0a0a0a] border-y border-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {izmirStats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#FF6600] mb-2">{s.number}</div>
                <div className="text-sm md:text-base text-gray-400 uppercase tracking-wide">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Services in Izmir</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">Everything your engine needs</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Card key={i} className="bg-[#1a1a1a] border-gray-800 hover:border-[#FF6600] transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-[#FF6600]/20 rounded-full flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-[#FF6600]" />
                    </div>
                    <h3 className="text-white font-semibold text-lg mb-2">{s.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Embedded Calculator */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Try it now</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">Discover your vehicle's true potential</h2>
            <p className="text-gray-400 mt-3 max-w-2xl mx-auto">Instant Stage 1 gain estimates from our real tuning database.</p>
          </div>
          <CalculatorWidget />
        </div>
      </section>

      {/* Location block */}
      <section id="location" className="py-20 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start max-w-6xl mx-auto">
            <div>
              <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Visit us</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-2 mb-6">Powerlab Works HQ Türkiye</h2>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-[#FF6600] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Address</p>
                    <p>Kazımdirik Mahallesi 283. Sokak No:3, Bornova / İzmir</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-[#FF6600] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Phone / WhatsApp</p>
                    <a href="tel:+905327866635" className="hover:text-[#FF6600]">+90 532 786 66 35</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-[#FF6600] mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-white">Email</p>
                    <a href="mailto:info@powerlab.works" className="hover:text-[#FF6600]">info@powerlab.works</a>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Button onClick={() => window.open(WA_URL, '_blank')} className="bg-[#25D366] hover:bg-[#1DA851] text-white">
                  <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp Us
                </Button>
                <Button onClick={() => window.location.href = 'mailto:info@powerlab.works?subject=Izmir%20Appointment'} className="bg-[#FF6600] hover:bg-[#E55A00] text-white">
                  Book Appointment
                </Button>
              </div>
            </div>
            <div className="relative w-full h-[400px] rounded-lg overflow-hidden border border-gray-800 shadow-2xl">
              <iframe
                title="Powerlab Works HQ Türkiye - Bornova, İzmir"
                src="https://www.google.com/maps?q=Kaz%C4%B1mdirik%20Mahallesi%20283.%20Sokak%20No%3A3%20Bornova%20%C4%B0zmir&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">What Our Clients Say</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {testimonials.map(tr => (
              <Card key={tr.id} className="bg-[#1a1a1a] border-gray-800">
                <CardContent className="p-6">
                  <div className="flex mb-4">
                    {[...Array(tr.rating)].map((_, i) => <Star key={i} className="w-5 h-5 fill-[#FF6600] text-[#FF6600]" />)}
                  </div>
                  <p className="text-gray-300 mb-4 italic">"{tr.text}"</p>
                  <div className="border-t border-gray-800 pt-4">
                    <p className="text-white font-semibold">{tr.name}</p>
                    <p className="text-gray-500 text-sm">{tr.vehicle}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ - AI-friendly, all answers visible for maximum extraction by AI search */}
      <section id="faq" className="py-20 bg-[#0a0a0a]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-[#FF6600] font-semibold text-sm uppercase tracking-wider">Frequently Asked Questions</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">Izmir chip tuning — answered</h2>
            </div>
            <div className="space-y-4">
              {faqIzmirEn.map((item, i) => (
                <Card key={i} className="bg-[#1a1a1a] border-gray-800" data-testid={`faq-item-${i}`}>
                  <CardContent className="p-6">
                    <h3 className="text-white font-semibold text-lg mb-3">{item.q}</h3>
                    <p className="text-gray-300 leading-relaxed">{item.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-900 py-10">
        <div className="container mx-auto px-4 text-center">
          <img src={LOGO_URL} alt="Powerlab" className="h-12 w-auto object-contain mx-auto mb-4" />
          <p className="text-gray-500 text-sm mb-2">Powerlab Works OU • Estonia • Powerlab Works HQ Türkiye — Bornova, İzmir</p>
          <p className="text-gray-600 text-xs">© 2025 Powerlab Works OU. All rights reserved.</p>
          <LegalLinks lang="en" className="mt-3" />
          <p className="mt-3">
            <Link to="/tr/izmir-chip-tuning" className="text-[#FF6600] hover:underline text-sm" hrefLang="tr">Türkçe versiyon &rarr;</Link>
          </p>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a
        href={WA_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 md:top-1/2 md:bottom-auto md:-translate-y-1/2 bg-[#25D366] hover:bg-[#1DA851] text-white p-4 rounded-full shadow-2xl z-40 transition-all hover:scale-110 flex items-center justify-center"
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle className="w-6 h-6" />
      </a>
    </div>
  );
};

export default IzmirChipTuning;
